import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

export interface PredictionCost {
  _id: string;
  description: string;
  amountUsd: number;
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface CostsMonthlyEntry {
  month: string;
  totalUsd: number;
  count: number;
}

export interface CostsMonthlyResponse {
  monthly: CostsMonthlyEntry[];
  totalAllTime: number;
}

export interface RealPnlResponse {
  pnlUsd: number;
  totalCostsUsd: number;
  realPnlUsd: number;
  monthly: CostsMonthlyEntry[];
}

export interface PartnerPayment {
  _id: string;
  settledAt: string;
  paymentUsd: number;
  paymentSource: 'platform' | 'external';
  paymentPlatform?: 'kalshi' | 'polymarket';
  note?: string;
  realPnlSnapshotUsd: number;
  portfolioPnlSnapshotUsd?: number;
  totalCostsSnapshotUsd?: number;
  snapshotIsManual: boolean;
  periodStart: string;
  periodEnd: string;
  periodRealPnlUsd: number;
  createdAt: string;
  updatedAt: string;
}

export interface CurrentPeriod {
  startDate: string;
  baselineRealPnlUsd: number;
  currentRealPnlUsd: number;
  periodRealPnlUsd: number;
}

export interface PartnerPaymentsResponse {
  payments: PartnerPayment[];
  currentPeriod: CurrentPeriod;
  totalPaidUsd: number;
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries('prediction-costs');
  qc.invalidateQueries('prediction-costs-monthly');
  qc.invalidateQueries('prediction-real-pnl');
};

export function useCosts() {
  return useQuery(['prediction-costs'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionCost[]>(
        '/positions/costs'
      );
    return data;
  });
}

export function useCostsMonthly() {
  return useQuery(['prediction-costs-monthly'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<CostsMonthlyResponse>(
        '/positions/costs/monthly'
      );
    return data;
  });
}

export function useRealPnl() {
  return useQuery(
    ['prediction-real-pnl'],
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.get<RealPnlResponse>(
          '/positions/real-pnl'
        );
      return data;
    },
    { refetchInterval: 30000 }
  );
}

export function useCreateCost() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { description: string; amountUsd: number; date?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        '/positions/costs',
        body
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

export function useUpdateCost() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      ...body
    }: {
      id: string;
      description?: string;
      amountUsd?: number;
      date?: string;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/costs/${id}`,
        body
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

export function useDeleteCost() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.delete(
        `/positions/costs/${id}`
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

// Recording/deleting a payment can auto-create or remove a withdrawal
// cashflow, which shifts the performance baseline — refresh real-pnl and
// cashflows alongside the payments list.
const invalidatePayments = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries('prediction-partner-payments');
  qc.invalidateQueries('prediction-real-pnl');
  qc.invalidateQueries('position-cashflows');
};

export function usePartnerPayments() {
  return useQuery(
    ['prediction-partner-payments'],
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.get<PartnerPaymentsResponse>(
          '/positions/payments'
        );
      return data;
    },
    { refetchInterval: 30000 }
  );
}

export function useCreatePartnerPayment() {
  const qc = useQueryClient();
  return useMutation(
    async (body: {
      paymentUsd: number;
      paymentSource: string;
      paymentPlatform?: string;
      settledAt?: string;
      note?: string;
      monthPnlUsd?: number;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        '/positions/payments',
        body
      );
      return data;
    },
    { onSuccess: () => invalidatePayments(qc) }
  );
}

export function useDeletePartnerPayment() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.delete(
        `/positions/payments/${id}`
      );
      return data;
    },
    { onSuccess: () => invalidatePayments(qc) }
  );
}

// Daily P&L snapshot — one row written by the backend cron at 23:59 UTC.
// Attribution fields (`markValueDeltaUsd`, `tradingCashDeltaUsd`, `dailyPnlUsd`)
// are null on the very first snapshot (no prior baseline).
export interface PositionSnapshot {
  pairId?: string;
  platform: 'kalshi' | 'polymarket';
  marketId?: string;
  side: 'yes' | 'no';
  contracts: number;
  avgPrice: number;
  markPrice: number;
  markValueUsd: number;
}

export interface PredictionDailySnapshot {
  date: string;
  positions: PositionSnapshot[];
  openPositionsMarkValueUsd: number;
  openPositionsCostBasisUsd: number;
  kalshiCashUsd: number;
  polymarketCashUsd: number;
  exchangeCashUsd: number;
  cashflowsUsd: number;
  totalPortfolioValueUsd: number;
  markValueDeltaUsd: number | null;
  tradingCashDeltaUsd: number | null;
  dailyPnlUsd: number | null;
}

export function useDailyPnl(from?: string, to?: string) {
  return useQuery(
    ['prediction-daily-pnl', from ?? null, to ?? null],
    async () => {
      const params: Record<string, string> = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PredictionDailySnapshot[]
      >('/positions/daily-pnl', { params });
      return data;
    },
    { refetchInterval: 30000 }
  );
}
